import { queryOptions } from '@tanstack/react-query'
import { apiPost, apiPostForm, apiGet } from './client'

/**
 * Feedbackul despre produs: `/feature_requests` si `/bug_reports`.
 *
 * Perechile vechi, din `application/controllers/Product_feedback.php`:
 *
 *   index($kind)      -> GET  /product_feedback/{kind}
 *   submit            -> POST /product_feedback/submit          (multipart)
 *   advance           -> POST /product_feedback/advance
 *   relayRetry($id)   -> POST /product_feedback/relay/{id}/retry
 *   relayDismiss($id) -> POST /product_feedback/relay/{id}/dismiss
 *   analyse           -> nemutat; vezi `App\Feedback\ProductFeedback::analyse()`
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * ADRESELE PUBLICE raman cele din `routes.php`: `/app/feature_requests` si
 * `/app/bug_reports`. Sunt doua din cele trei legaturi din SUBSOLUL fiecarei
 * pagini, deci ele nu se muta si nu se redenumesc.
 *
 * NIMIC NU SE ASCUNDE DIN ECRAN. `canOpen` pe fiecare rand, `canOperate` si
 * `canWorkTickets` pe pagina vin gata hotarate de la server, ca in portal;
 * front-end-ul nu stie drepturile omului si nu le ghiceste.
 */

export const KINDS = ['feature', 'bug'] as const

export type FeedbackKind = (typeof KINDS)[number]

export function isFeedbackKind(value: string): value is FeedbackKind {
  return (KINDS as readonly string[]).includes(value)
}

/** O poza de pe tichet: legatura mare si miniatura. */
export type FeedbackShot = {
  filename: string
  url: string
  thumbUrl: string
  /**
   * Setarea care lipseste cand adresele ies goale (`DOWNLOAD_SERVER`,
   * `OD_LEGACY_CRYPT_KEY`); '' cand nu e o setare - cu fisierele pe update
   * nu se pomeneste DOWNLOAD_SERVER. Lipsa la raspunsurile vechi.
   */
  missing?: string
}

/** Un fisier care nu e poza - se citeste ca o lista, nu ca un dreptunghi gol. */
export type FeedbackFile = {
  filename: string
  size: number
  url: string
}

/** Un raspuns de pe tichet. `comment` e HTML scris in editor - vezi pagina. */
export type FeedbackComment = {
  id: number
  author: string
  created: string
  comment: string
}

/** Un stadiu in care poate trece randul - chiar un pas al fluxului. */
export type FeedbackStep = {
  id: number
  name: string
}

export type FeedbackRequest = {
  id: number
  subject: string
  /** Deja fara marcaje, ca in macheta veche. */
  description: string
  created: string
  updated: string | null
  resolved: string | null
  closed: boolean
  statusName: string
  statusColor: string
  authorName: string
  isMine: boolean
  /** Hotarat pe server: al tau, sau ai dreptul de citire pe tichete. */
  canOpen: boolean
  shots: FeedbackShot[]
  files: FeedbackFile[]
  comments: FeedbackComment[]
  nextStatuses: FeedbackStep[]
}

/** O cerere scrisa acasa, cat timp nu exista categoria de tichete. */
export type RelayedRequest = {
  id: number
  subject: string
  description: string
  created: string
  sent: string | null
  attempts: number
  lastError: string
  userName: string
  isMine: boolean
  stuck: boolean
}

export type FeedbackCounts = {
  open: number
  resolved: number
  total: number
}

export type FeedbackMonth = {
  ym: string
  label: string
  asked: number
  resolved: number
}

export type FeedbackEffort = {
  monthly: FeedbackMonth[]
  avgHours: number | null
  oldestOpenHours: number | null
}

export type ProductFeedbackPage = {
  kind: FeedbackKind
  kindLabel: string
  /** Exista categoria si subcategoria felului asta? */
  ready: boolean
  categoryName: string
  subcategoryName: string
  counts: FeedbackCounts
  mine: FeedbackCounts
  requests: FeedbackRequest[]
  effort: FeedbackEffort
  relayed: RelayedRequest[]
  canWorkTickets: boolean
  canOperate: boolean
  /** Cititul pozei de catre model - nemutat inca, deci mereu fals. */
  aiReady: boolean
  version: string
}

export function productFeedbackQuery(kind: FeedbackKind) {
  return queryOptions({
    queryKey: ['product_feedback', kind],
    queryFn: () => apiGet<ProductFeedbackPage>(`/product_feedback/${kind}`),
  })
}

export type SubmitAnswer = {
  status: string
  id?: number
  attached?: number
  relayed?: boolean
  message: string
}

/**
 * Cererea noua. MULTIPART, fiindca poate purta capturi de ecran - acelasi plic
 * ca al formularului vechi (`images[]`).
 */
export function submitFeedback(fields: { kind: FeedbackKind; title: string; description: string }, images: File[]) {
  const body = new FormData()
  body.append('kind', fields.kind)
  body.append('title', fields.title)
  body.append('description', fields.description)
  images.forEach((file) => body.append('images[]', file, file.name))

  return apiPostForm<SubmitAnswer>('/product_feedback/submit', body)
}

/** Mutarea stadiului, pe drumul obisnuit al tichetelor. */
export function advanceFeedback(ticket: number, status: number) {
  return apiPost<{ status: string; message: string }>('/product_feedback/advance', { ticket, status })
}

/** Mai incearca ACUM o cerere care n-a apucat sa plece. */
export function retryRelay(id: number) {
  return apiPost<{ status: string; message: string }>(`/product_feedback/relay/${id}/retry`, {})
}

/** Nu se mai bate la usa. Randul RAMANE scris. */
export function dismissRelay(id: number) {
  return apiPost<{ status: string; message: string }>(`/product_feedback/relay/${id}/dismiss`, {})
}
